import { useAuth0 } from '@auth0/auth0-react';
import useGetAccessToken from 'components/Dashboard/apiCalls/useGetAccessToken';
import { netApiClient } from 'api/apiClient';
import type { AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';

declare module 'axios' {
  export interface AxiosRequestConfig {
    notShowErrorMessage?: boolean;
    errorMessage?: string;
  }
}

export interface RequestOptions extends Omit<AxiosRequestConfig, 'url'> {
  pathname: string;
  authenticated?: boolean;
  client?: typeof netApiClient;
}

export type RequestFn = (options: RequestOptions) => Promise<AxiosResponse>;

const useApiRequest = () => {
  const { isAuthenticated } = useAuth0();
  const { getAccessToken } = useGetAccessToken();

  const request = async ({
    method,
    pathname,
    data,
    signal,
    authenticated = true,
    client = netApiClient,
    notShowErrorMessage,
    errorMessage,
    headers: callerHeaders,
    ...axiosOptions
  }: RequestOptions): Promise<AxiosResponse> => {
    const token = authenticated && isAuthenticated ? await getAccessToken() : null;
    // ponytail: yield a microtask so StrictMode's synchronous mount->cleanup->mount
    // can abort the signal before we dispatch, same as the authenticated branch
    // (which already yields on the getAccessToken await) — keeps both paths symmetric.
    await Promise.resolve();
    try {
      return await client.request({
        method,
        url: pathname,
        data,
        signal,
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...callerHeaders,
        },
        notShowErrorMessage,
        errorMessage,
        ...axiosOptions,
      });
    } catch (error) {
      const err = error as AxiosError;
      if (err.name === 'CanceledError') {
        console.error((err?.config?.signal as AbortSignal)?.reason);
      }
      throw error;
    }
  };

  return { request };
};

export default useApiRequest;
